/**
 * Constants for real estate types, classes, and other parameters
 * Single source of truth for use across all components
 */

// Interfaces for categories and subcategories
export interface PropertyCategory {
  value: string;
  label: string;
}

export interface PropertySubcategory {
  value: string;
  label: string;
}

export interface PropertyCategoryData {
  categoryName: string;
  subcategories: PropertySubcategory[];
}

export interface PropertyType {
  category: string;
  categoryName: string;
  subcategories: string[];
  subcategoriesName: string[];
}

// Real estate categories - updated according to PropertyType enum
export const PROPERTY_CATEGORIES = [
  { value: "primary_residential", label: "Primary Residential" },
  { value: "primary_commercial", label: "Primary Commercial" },
  { value: "primary_hotels", label: "Primary Hotels" },
  { value: "buy_residential", label: "Buy Residential" },
  { value: "buy_commercial", label: "Buy Commercial" },
  { value: "buy_land", label: "Buy Land" },
  { value: "rent_residential", label: "Rent Residential" },
  { value: "rent_commercial", label: "Rent Commercial" },
  { value: "rent_land", label: "Rent Land" },
] as const;

export type PropertyCategoryValue =
  (typeof PROPERTY_CATEGORIES)[number]["value"];

// Data for categories with subcategories - updated according to ObjectType enum
export const PROPERTY_CATEGORIES_DATA: Record<
  PropertyCategoryValue,
  PropertyCategoryData
> = {
  primary_residential: {
    categoryName: "Primary Residential",
    subcategories: [
      { value: "apartment", label: "Apartment" },
      { value: "villa", label: "Villa" },
      { value: "detached_house", label: "Detached House" },
      { value: "studio", label: "Studio" },
      { value: "maisonette", label: "Maisonette" },
      { value: "cottage", label: "Cottage" },
      { value: "penthouse", label: "Penthouse" },
    ],
  },
  primary_commercial: {
    categoryName: "Primary Commercial",
    subcategories: [
      { value: "office", label: "Office" },
      { value: "parking_space", label: "Parking Space" },
    ],
  },
  primary_hotels: {
    categoryName: "Primary Hotels",
    subcategories: [
      { value: "hotel", label: "Hotel" },
      { value: "hotel_room", label: "Hotel Room" },
    ],
  },
  buy_residential: {
    categoryName: "Buy Residential",
    subcategories: [
      { value: "apartment", label: "Apartment" },
      { value: "villa", label: "Villa" },
      { value: "detached_house", label: "Detached House" },
      { value: "studio", label: "Studio" },
      { value: "maisonette", label: "Maisonette" },
      { value: "cottage", label: "Cottage" },
      { value: "penthouse", label: "Penthouse" },
      { value: "basement", label: "Basement" },
    ],
  },
  buy_commercial: {
    categoryName: "Buy Commercial",
    subcategories: [
      { value: "office", label: "Office" },
      { value: "store", label: "Store" },
      { value: "warehouse", label: "Warehouse" },
      { value: "hotel", label: "Hotel" },
      { value: "hotel_room", label: "Hotel Room" },
      { value: "business", label: "Business" },
      { value: "parking_space", label: "Parking Space" },
      { value: "restaurant_cafe_bar", label: "Restaurant Cafe Bar" },
      { value: "detached_building", label: "Detached Building" },
      { value: "factory_enterprise", label: "Factory Enterprise" },
      { value: "massage_therapist_office", label: "Massage Therapist Office" },
      { value: "dentist_office", label: "Dentist Office" },
      { value: "farm", label: "Farm" },
      { value: "service_station", label: "Service Station" },
      { value: "basement", label: "Basement" },
    ],
  },
  buy_land: {
    categoryName: "Buy Land",
    subcategories: [
      { value: "commercial_land", label: "Commercial Land" },
      { value: "residential_land", label: "Residential Land" },
      { value: "parcel", label: "Parcel" },
      { value: "island", label: "Island" },
    ],
  },
  rent_residential: {
    categoryName: "Rent Residential",
    subcategories: [
      { value: "apartment", label: "Apartment" },
      { value: "villa", label: "Villa" },
      { value: "detached_house", label: "Detached House" },
      { value: "studio", label: "Studio" },
      { value: "maisonette", label: "Maisonette" },
      { value: "cottage", label: "Cottage" },
      { value: "penthouse", label: "Penthouse" },
    ],
  },
  rent_commercial: {
    categoryName: "Rent Commercial",
    subcategories: [
      { value: "office", label: "Office" },
      { value: "store", label: "Store" },
      { value: "warehouse", label: "Warehouse" },
      { value: "hotel_room", label: "Hotel Room" },
      { value: "business", label: "Business" },
      { value: "parking_space", label: "Parking Space" },
      { value: "restaurant_cafe_bar", label: "Restaurant Cafe Bar" },
      { value: "detached_building", label: "Detached Building" },
      { value: "factory_enterprise", label: "Factory Enterprise" },
      { value: "massage_therapist_office", label: "Massage Therapist Office" },
      { value: "dentist_office", label: "Dentist Office" },
      { value: "farm", label: "Farm" },
      { value: "service_station", label: "Service Station" },
      { value: "basement", label: "Basement" },
    ],
  },
  rent_land: {
    categoryName: "Rent Land",
    subcategories: [
      { value: "commercial_land", label: "Commercial Land" },
      { value: "residential_land", label: "Residential Land" },
      { value: "parcel", label: "Parcel" },
      { value: "island", label: "Island" },
    ],
  },
};

// Real estate classes
export const PROPERTY_CLASSES = [
  { value: "economy", label: "Economy" },
  { value: "standard", label: "Standard" },
  { value: "comfort", label: "Comfort" },
  { value: "business", label: "Business" },
  { value: "elite", label: "Elite" },
] as const;

export type PropertyClassValue = (typeof PROPERTY_CLASSES)[number]["value"];

// Number of rooms (for RoomsType)
export const ROOMS_COUNT_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
export type RoomsCountValue = (typeof ROOMS_COUNT_VALUES)[number];

// Number of bedrooms (for BedroomsType)
export const BEDROOMS_COUNT_VALUES = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10,
] as const;
export type BedroomsCountValue = (typeof BEDROOMS_COUNT_VALUES)[number];

// Number of bathrooms (for BathroomsType)
export const BATHROOMS_COUNT_VALUES = [1, 2, 3, 4] as const;
export type BathroomsCountValue = (typeof BATHROOMS_COUNT_VALUES)[number];

// Frame types/building materials (building frames)
export const FRAME_TYPES = [
  { value: "white_frame", label: "White" },
  { value: "black_frame", label: "Black" },
  { value: "green_frame", label: "Green" },
  { value: "requires_renovation", label: "Requires Renovation" },
] as const;

export type FrameTypeValue = (typeof FRAME_TYPES)[number]["value"];

// Heating types
export const HEATING_TYPES = [
  { value: "autonomous", label: "Autonomous" },
  { value: "electric", label: "Electric" },
  { value: "gas", label: "Gas" },
  { value: "centralized", label: "Central" },
] as const;

export type HeatingTypeValue = (typeof HEATING_TYPES)[number]["value"];

// Wall types
export const WALL_TYPES = [
  { value: "brick", label: "Brick" },
  { value: "monolith", label: "Monolith" },
  { value: "ceratherm", label: "Keratrem" },
] as const;

export type WallTypeValue = (typeof WALL_TYPES)[number]["value"];

// Insulation types
export const INSULATION_TYPES = [
  { value: "mineral_wool", label: "Mineral wool" },
  { value: "basalt_wool", label: "Basalt wool" },
] as const;

export type InsulationTypeValue = (typeof INSULATION_TYPES)[number]["value"];

// Condition types
export const CONDITION_TYPES = [
  { value: "requires_renovation", label: "Renovation needed" },
  { value: "finished", label: "Finished" },
  { value: "furnished", label: "Furnished" },
] as const;

export type ConditionTypeValue = (typeof CONDITION_TYPES)[number]["value"];

// Renovation types
export const RENOVATION_TYPES = [
  { value: "author", label: "Author" },
  { value: "euro", label: "Euro" },
  { value: "cosmetic", label: "Cosmetic" },
  { value: "livable", label: "Living condition" },
  { value: "after_construction", label: "After builders" },
  { value: "clean_finish", label: "Shell and core" },
  { value: "emergency", label: "Emergency" },
] as const;

export type RenovationTypeValue = (typeof RENOVATION_TYPES)[number]["value"];

// Parking types
export const PARKING_TYPES = [
  { value: "underground", label: "Underground" },
  { value: "ground", label: "Ground" },
  { value: "none", label: "None" },
] as const;

export type ParkingTypeValue = (typeof PARKING_TYPES)[number]["value"];

// Currencies
export const CURRENCIES = [
  { value: "USD", label: "USD" },
  { value: "EUR", label: "EUR" },
  { value: "UAH", label: "UAH" },
  { value: "GEL", label: "GEL" },
] as const;

export type CurrencyValue = (typeof CURRENCIES)[number]["value"];

// Construction status
export const CONSTRUCTION_STATUSES = [
  { value: "not_started", label: "ConstructionStatusNotStarted" },
  { value: "in_progress", label: "ConstructionStatusInProgress" },
  { value: "ready", label: "ConstructionStatusReady" },
] as const;

export type ConstructionStatusValue =
  (typeof CONSTRUCTION_STATUSES)[number]["value"];

// Project types
export const PROJECT_TYPES = [
  { value: "residential_complex", label: "Residential complex" },
  { value: "villa", label: "Villa" },
  { value: "cottage_village", label: "Cottage village" },
] as const;

export type ProjectTypeValue = (typeof PROJECT_TYPES)[number]["value"];

// Layout statuses
export const LAYOUT_STATUSES = [
  { value: "active", label: "Available" },
  { value: "sold", label: "Sold" },
  { value: "hidden", label: "Hidden" },
] as const;

export type LayoutStatusValue = (typeof LAYOUT_STATUSES)[number]["value"];

// Call statuses
export const CALL_STATUSES = [
  { value: "not_called", label: "Not Called" },
  { value: "called_no_answer", label: "Called - No Answer" },
  { value: "called_interested", label: "Called - Interested" },
  { value: "called_not_interested", label: "Called - Not Interested" },
  { value: "wrong_number", label: "Wrong Number" },
] as const;

export type CallStatusValue = (typeof CALL_STATUSES)[number]["value"];

// Listing tags
export const LISTING_TAGS = [
  { value: "exclusive", label: "Exclusive" },
  { value: "added", label: "Added" },
  { value: "no_answer", label: "No Answer" },
  { value: "hot", label: "Hot" },
  { value: "premium", label: "Premium" },
] as const;

export type ListingTagValue = (typeof LISTING_TAGS)[number]["value"];

// Helper functions for validation
export const isValidRoomsCount = (value: number): boolean =>
  ROOMS_COUNT_VALUES.includes(value as RoomsCountValue);

export const isValidBedroomsCount = (value: number): boolean =>
  BEDROOMS_COUNT_VALUES.includes(value as BedroomsCountValue);

export const isValidBathroomsCount = (value: number): boolean =>
  BATHROOMS_COUNT_VALUES.includes(value as BathroomsCountValue);

// Quarters for completion dates
export const COMPLETION_QUARTERS = [
  { value: 1, label: "1st Quarter" },
  { value: 2, label: "2nd Quarter" },
  { value: 3, label: "3rd Quarter" },
  { value: 4, label: "4th Quarter" },
] as const;

export type CompletionQuarterValue =
  (typeof COMPLETION_QUARTERS)[number]["value"];

// Years for completion dates
export const COMPLETION_YEARS = [
  { value: 2024, label: "2024" },
  { value: 2025, label: "2025" },
  { value: 2026, label: "2026" },
  { value: 2027, label: "2027" },
  { value: 2028, label: "2028" },
  { value: 2029, label: "2029" },
] as const;

export type CompletionYearValue = (typeof COMPLETION_YEARS)[number]["value"];

// Finance options
export const FINANCE_OPTIONS = [
  { value: "installment", label: "Installment payment" },
  { value: "mortgage", label: "Mortgage" },
  { value: "eOselya", label: "eOselya" },
] as const;

export type FinanceOptionValue = (typeof FINANCE_OPTIONS)[number]["value"];
